import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { ExamRoom, ExamRoomBlock } from '@/types/settings-api';

/**
 * Save the practice's exam-room list (PROD-790 follow-up).
 *
 *   POST /api/Practice/CreateExamRoom
 *
 * The wire is a **full-state replace**, NOT a diff. Send the desired
 * post-save `examRooms[]` plus `deletedId[]` for explicit removals.
 * Existing rooms that are still wanted MUST be in `examRooms[]` —
 * omitting an existing room does NOT delete it (only `deletedId[]`
 * does), but the backend's full-replace semantics mean any room not
 * present + not in deletedId becomes orphaned. Always ship the full
 * desired state.
 *
 * Verified empirically against 2 captured cURLs 2026-05-25:
 *   - POST 1: 59 rooms with 1 new (id:null "test room") → response added
 *     `da7f09b5-…` to the new row.
 *   - POST 2: same 59 rooms, "test room" renamed to "test room 2" (same
 *     id `da7f09b5-…`) → response echoed the rename.
 *
 * Response is an ARRAY of ExamRoomBlock (length 1) — matching the GET
 * shape under `practiceRoomDetail[]`. We return the first block to
 * consumers; the wrapper-array is a wire artifact.
 *
 * Cache: auto-invalidates the parent practice query so the rooms list
 * round-trips back via the next `usePractice(id)` read.
 */
export interface SaveExamRoomsInput {
  practiceId: string;
  /** Server-managed counter; pass through verbatim from the original GET. */
  noOfExamHours: number;
  /**
   * Desired post-save room list. New rows have `examHourId: null` (the
   * backend allocates a GUID). Renamed rows keep their `examHourId` +
   * change `examRoom`. Existing rows the user wants to keep stay in
   * this array unchanged.
   */
  examRooms: ExamRoom[];
  /** Ids of rooms removed since the original GET. */
  deletedId: string[];
}

export function useSaveExamRooms() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: SaveExamRoomsInput,
    ): Promise<ExamRoomBlock> => {
      const body: ExamRoomBlock = {
        practiceId: input.practiceId,
        noOfExamHours: input.noOfExamHours,
        examRooms: input.examRooms,
        deletedId: input.deletedId,
      };
      const result = await post<ExamRoomBlock[]>(
        '/api/Practice/CreateExamRoom',
        body,
      );
      // Response is `ExamRoomBlock[]` of length 1 — unwrap for consumers.
      return result[0];
    },
    onSuccess: (_data, vars) => {
      // The practice GET (`/api/v1/Practice/Details/{id}`) includes
      // `practiceRoomDetail[0]`. Invalidate the parent practice cache so
      // the next render refetches with the server-canonical state +
      // freshly-allocated examHourIds on new rows.
      void queryClient.invalidateQueries({
        queryKey: ['settings', 'practice-by-id', vars.practiceId],
      });
    },
  });
}
